import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Container, Dropdown, Navbar } from 'react-bootstrap';
import { 
  Pen, People, ShieldShaded, Coin, Tree, Globe2, ReceiptCutoff, 
  ChevronRight, Person 
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import HomeProvinceButton from '../notifications/HomeProvinceButton';
import MexicanHomeStateButton from '../notifications/MexicanHomeStateButton';
import UKHomeRegionButton from '../notifications/UKHomeRegionButton';
import HomeStateButton from '../notifications/HomeStateButton';
import NotificationButtonNotificationPage from '../notifications/NotificationButtonNotificationPage';
import BottomNavbar from '../navigation/Menu';

const handleHapticsClick = async (callback) => {
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (error) {}
  callback();
  window.scrollTo(0, 0);
};

const NotificationSettingsPage = () => {
  const initialCountry = localStorage.getItem('homeCountry') || 'us';
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [notificationSettings, setNotificationSettings] = useState({});

  // PRODUCTION topic names - Format: {Country}{Branch}News
  const countryConfig = useMemo(() => ({
    us: {
      schema: 'united_states_of_america',
      displayName: 'United States',
      flag: '🇺🇸',
      topicKeys: {
        legislative: 'LegislativeNews',
        executive: 'ExecutiveNews',
        judicial: 'JudicialNews',
        defense: 'DefenseNews',
        economy: 'EconomyNews',
        elections: 'ElectionNews',
        environment: 'EnvironmentNews',
        foreignaffairs: 'ForeignAffairsNews',
        health: 'HealthNews',
        science_and_tech: 'ScienceTechNews',
        spending: 'SpendingNews',
        infrastructure: 'InfrastructureNews',
        regulations: 'RegulationsNews',
        justice: 'JusticeNews'
      }
    },
    ca: {
      schema: 'canada',
      displayName: 'Canada',
      flag: '🇨🇦',
      topicKeys: {
        legislative: 'CanadaLegislativeNews',
        executive: 'CanadaExecutiveNews',
        judicial: 'CanadaJudicialNews',
        defense: 'CanadaDefenseNews',
        economy: 'CanadaEconomyNews',
        elections: 'CanadaElectionNews',
        environment: 'CanadaEnvironmentNews',
        foreignaffairs: 'CanadaForeignAffairsNews',
        health: 'CanadaHealthNews',
        science_and_tech: 'CanadaScienceTechNews',
        spending: 'CanadaSpendingNews',
        infrastructure: 'CanadaInfrastructureNews',
        regulations: 'CanadaRegulationsNews',
        justice: 'CanadaJusticeNews'
      }
    },
    mx: {
      schema: 'mexico',
      displayName: 'Mexico',
      flag: '🇲🇽',
      topicKeys: {
        legislative: 'MexicoLegislativeNews',
        executive: 'MexicoExecutiveNews',
        judicial: 'MexicoJudicialNews',
        defense: 'MexicoDefenseNews',
        economy: 'MexicoEconomyNews',
        environment: 'MexicoEnvironmentNews',
        foreignaffairs: 'MexicoForeignAffairsNews',
        health: 'MexicoHealthNews',
        infrastructure: 'MexicoInfrastructureNews'
      }
    },
    uk: {
      schema: 'united_kingdom',
      displayName: 'United Kingdom',
      flag: '🇬🇧',
      topicKeys: {
        legislative: 'UKLegislativeNews',
        executive: 'UKExecutiveNews',
        judicial: 'UKJudicialNews',
        defense: 'UKDefenseNews',
        economy: 'UKEconomyNews',
        environment: 'UKEnvironmentNews',
        foreignaffairs: 'UKForeignAffairsNews',
        health: 'UKHealthNews',
        science_and_tech: 'UKScienceTechNews',
        justice: 'UKJusticeNews',
        infrastructure: 'UKInfrastructureNews'
      }
    },
    australia: { schema: 'public', displayName: 'Australia', flag: '🇦🇺', isInternational: true },
    france: { schema: 'public', displayName: 'France', flag: '🇫🇷', isInternational: true },
    italy: { schema: 'public', displayName: 'Italy', flag: '🇮🇹', isInternational: true },
    japan: { schema: 'public', displayName: 'Japan', flag: '🇯🇵', isInternational: true },
    'south-africa': { schema: 'public', displayName: 'South Africa', flag: '🇿🇦', isInternational: true },
    ukraine: { schema: 'public', displayName: 'Ukraine', flag: '🇺🇦', isInternational: true }
  }), []);

  const currentConfig = useMemo(() => {
    return countryConfig[selectedCountry] || countryConfig.us;
  }, [selectedCountry, countryConfig]);

  useEffect(() => {
    const loadNotificationSettings = () => {
      const settings = {};
      if (currentConfig.topicKeys) {
        Object.values(currentConfig.topicKeys).forEach(topicKey => {
          settings[topicKey] = localStorage.getItem(topicKey) === 'true';
        });
      }
      setNotificationSettings(settings);
    };
    loadNotificationSettings();
  }, [currentConfig]);

  useEffect(() => {
    const handleStorageChange = () => {
      const updatedCountry = localStorage.getItem('homeCountry') || 'us';
      setSelectedCountry(updatedCountry);
    };
    window.addEventListener('storage', handleStorageChange);
    handleStorageChange();
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleCountryChange = useCallback((country) => {
    setSelectedCountry(country);
    localStorage.setItem('homeCountry', country);
  }, []);

  const handleNotificationChange = useCallback((storageKey, newValue) => {
    setNotificationSettings(prev => ({
      ...prev,
      [storageKey]: newValue
    }));
  }, []);

  const icons = useMemo(() => ({
    Judicial: () => (
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
        <path d="M160-120v-80h480v80H160Zm226-194L160-540l84-86 228 226-86 86Zm254-254L414-796l86-84 226 226-86 86Zm184 408L302-682l56-56 522 522-56 56Z"/>
      </svg>
    ),
    Health: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="m8 2.748-.717-.737C5.6.281 2.514.878 1.4 3.053.918 3.995.78 5.323 1.508 7H.43c-2.128-5.697 4.165-8.83 7.394-5.857q.09.083.176.171a3 3 0 0 1 .176-.17c3.23-2.974 9.522.159 7.394 5.856h-1.078c.728-1.677.59-3.005.108-3.947C13.486.878 10.4.28 8.717 2.01zM2.212 10h1.315C4.593 11.183 6.05 12.458 8 13.795c1.949-1.337 3.407-2.612 4.473-3.795h1.315c-1.265 1.566-3.14 3.25-5.788 5-2.648-1.75-4.523-3.434-5.788-5"/>
        <path d="M10.464 3.314a.5.5 0 0 0-.945.049L7.921 8.956 6.464 5.314a.5.5 0 0 0-.88-.091L3.732 8H.5a.5.5 0 0 0 0 1H4a.5.5 0 0 0 .416-.223l1.473-2.209 1.647 4.118a.5.5 0 0 0 .945-.049l1.598-5.593 1.457 3.642A.5.5 0 0 0 12 9h3.5a.5.5 0 0 0 0-1h-3.162z"/>
      </svg>
    ),
    ScienceTech: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M9.752 6.193c.599.6 1.73.437 2.528-.362.798-.799.96-1.932.362-2.531-.599-.6-1.73-.438-2.528.361-.798.8-.96 1.933-.362 2.532Z"/>
        <path d="M15.811 3.312c-.363 1.534-1.334 3.626-3.64 6.218l-.24 2.408a2.56 2.56 0 0 1-.732 1.526L8.817 15.85a.51.51 0 0 1-.867-.434l.27-1.899c.04-.28-.013-.593-.131-.956a9.42 9.42 0 0 0-.249-.657l-.082-.202c-.815-.197-1.578-.662-2.191-1.277-.614-.615-1.079-1.379-1.275-2.195l-.203-.083a9.556 9.556 0 0 0-.655-.248c-.363-.119-.675-.172-.955-.132l-1.896.27A.51.51 0 0 1 .15 7.17l2.382-2.386c.41-.41.947-.67 1.524-.734h.006l2.4-.238C9.005 1.55 11.087.582 12.623.208c.89-.217 1.59-.232 2.08-.188.244.023.435.06.57.093.067.017.12.033.16.045.184.06.279.13.351.295l.029.073a3.475 3.475 0 0 1 .157.721c.055.485.051 1.178-.159 2.065Z"/>
        <path d="M7.009 12.139a7.632 7.632 0 0 1-1.804-1.352A7.568 7.568 0 0 1 3.794 8.86c-1.102.992-1.965 5.054-1.839 5.18.125.126 3.936-.896 5.054-1.902Z"/>
      </svg>
    ),
    Infrastructure: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M4 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5z"/>
        <path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1zm11 0H3v14h3v-2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V15h3z"/>
      </svg>
    ),
    Justice: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3"/>
        <path d="m5.93 6.704-.846 8.451a.768.768 0 0 0 1.523.203l.81-4.865a.59.59 0 0 1 1.165 0l.81 4.865a.768.768 0 0 0 1.523-.203l-.845-8.451A1.5 1.5 0 0 1 10.5 5.5L13 2.284a.796.796 0 0 0-1.239-.998L9.634 3.84a.7.7 0 0 1-.33.235c-.23.074-.665.176-1.304.176-.64 0-1.074-.102-1.305-.176a.7.7 0 0 1-.329-.235L4.239 1.286a.796.796 0 0 0-1.24.998l2.5 3.216c.317.316.475.758.43 1.204Z"/>
      </svg>
    ),
    Spending: () => (
      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
        <path d="M444-144v-80q-51-11-87.5-46T305-357l74-30q8 36 40.5 64.5T487-294q39 0 64-20t25-52q0-30-22.5-50T474-456q-78-28-114-61.5T324-604q0-50 32.5-86t87.5-47v-79h72v79q72 12 96.5 55t25.5 45l-70 29q-8-26-32-43t-53-17q-35 0-58 18t-23 44q0 26 25 44.5t93 41.5q70 23 102 60t32 94q0 57-37 96t-101 49v77h-72Z"/>
      </svg>
    ),
    Regulations: () => (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/>
        <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/>
      </svg>
    )
  }), []);

  const formatRegionName = useCallback((key, type) => {
    if (!key) return '';
    let formatted = key;
    formatted = formatted.replace(/(News|Updates)$/, '');
    formatted = formatted.replace(/([A-Z])/g, ' $1').trim();
    formatted = formatted.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
    const specialCases = {
      'newfoundland labrador': 'Newfoundland and Labrador',
      'northwest territories': 'Northwest Territories',
      'nova scotia': 'Nova Scotia',
      'prince edward island': 'Prince Edward Island',
      'british columbia': 'British Columbia',
      'new brunswick': 'New Brunswick',
      'new hampshire': 'New Hampshire',
      'new jersey': 'New Jersey',
      'new mexico': 'New Mexico',
      'new york': 'New York',
      'north carolina': 'North Carolina',
      'north dakota': 'North Dakota',
      'rhode island': 'Rhode Island',
      'south carolina': 'South Carolina',
      'south dakota': 'South Dakota',
      'west virginia': 'West Virginia',
      'northern ireland': 'Northern Ireland',
      'mexico city': 'Mexico City',
      'nuevo leon': 'Nuevo León',
      'san luis potosi': 'San Luis Potosí',
      'baja california': 'Baja California',
      'baja california sur': 'Baja California Sur',
      'quintana roo': 'Quintana Roo'
    };
    const lowerFormatted = formatted.toLowerCase();
    if (specialCases[lowerFormatted]) {
      formatted = specialCases[lowerFormatted];
    }
    return formatted;
  }, []);

  const homeRegionSection = useMemo(() => {
    const homeIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-house" viewBox="0 0 16 16">
        <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h9a1.5 1.5 0 0 0 1.5-1.5V8.207l.646.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293zM13 7.207V13.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V7.207l5-5z"/>
      </svg>
    );

    if (selectedCountry === 'us') {
      const homeStateValue = localStorage.getItem("homeState");
      if (homeStateValue) {
        const formattedName = formatRegionName(homeStateValue, 'state');
        return (
          <div key="home-state">
            <h5 id="notificationHeading">
              {homeIcon} {formattedName}
              <span style={{ float: 'right', marginRight: '10px', padding: '10px' }}>
                <HomeStateButton stateKey={homeStateValue} stateName={formattedName} />
              </span>
            </h5>
            <br />
          </div>
        );
      }
    } else if (selectedCountry === 'ca') {
      const homeProvinceValue = localStorage.getItem("homeProvince");
      if (homeProvinceValue) {
        const formattedName = formatRegionName(homeProvinceValue, 'province');
        return (
          <div key="home-province">
            <h5 id="notificationHeading">
              {homeIcon} {formattedName}
              <span style={{ float: 'right', marginRight: '10px', padding: '10px' }}>
                <HomeProvinceButton provinceKey={homeProvinceValue} provinceName={formattedName} />
              </span>
            </h5>
            <br />
          </div>
        );
      }
    } else if (selectedCountry === 'mx') {
      const homeMexicanStateValue = localStorage.getItem("homeMexicanState");
      if (homeMexicanStateValue) {
        const formattedName = formatRegionName(homeMexicanStateValue, 'state');
        return (
          <div key="home-mexican-state">
            <h5 id="notificationHeading">
              {homeIcon} {formattedName}
              <span style={{ float: 'right', marginRight: '10px', padding: '10px' }}>
                <MexicanHomeStateButton stateKey={homeMexicanStateValue} stateName={formattedName} />
              </span>
            </h5>
            <br />
          </div>
        );
      }
    } else if (selectedCountry === 'uk') {
      const homeUKRegionValue = localStorage.getItem("homeUKRegion");
      if (homeUKRegionValue) {
        const formattedName = formatRegionName(homeUKRegionValue, 'region');
        return (
          <div key="home-uk-region">
            <h5 id="notificationHeading">
              {homeIcon} {formattedName}
              <span style={{ float: 'right', marginRight: '10px', padding: '10px' }}>
                <UKHomeRegionButton regionKey={homeUKRegionValue} regionName={formattedName} />
              </span>
            </h5>
            <br />
          </div>
        );
      }
    }
    return null;
  }, [selectedCountry, formatRegionName]);

  const topicNotifications = useMemo(() => {
    if (currentConfig.isInternational) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
          <p>Notification settings for international countries are managed automatically.</p>
          <p>You will receive general news updates from {currentConfig.displayName}.</p>
        </div>
      );
    }

    if (!currentConfig.topicKeys) return null;

    const topics = [
      { key: 'legislative', name: 'Legislative', icon: <Pen /> },
      { key: 'executive', name: 'Executive', icon: <People /> },
      { key: 'judicial', name: 'Judicial', icon: <icons.Judicial /> },
      { key: 'defense', name: 'Defense', icon: <ShieldShaded /> },
      { key: 'economy', name: 'Economy', icon: <Coin /> },
      { key: 'elections', name: 'Elections', icon: <ReceiptCutoff /> },
      { key: 'environment', name: 'Environment', icon: <Tree /> },
      { key: 'foreignaffairs', name: 'Foreign Affairs', icon: <Globe2 /> },
      { key: 'health', name: 'Health', icon: <icons.Health /> },
      { key: 'science_and_tech', name: 'Science & Tech', icon: <icons.ScienceTech /> },
      { key: 'spending', name: 'Spending', icon: <icons.Spending /> },
      { key: 'infrastructure', name: 'Infrastructure', icon: <icons.Infrastructure /> },
      { key: 'regulations', name: 'Regulations', icon: <icons.Regulations /> },
      { key: 'justice', name: 'Justice', icon: <icons.Justice /> }
    ];

    return topics.map(topic => {
      const topicKey = currentConfig.topicKeys[topic.key];
      if (!topicKey) return null;

      return (
        <div key={topic.key}>
          <h5 id='notificationHeading'>
            {topic.icon} {topic.name}
            <NotificationButtonNotificationPage 
              notificationStorage={topicKey} 
              onToggle={handleNotificationChange}
            />
          </h5>
          <br />
        </div>
      );
    }).filter(Boolean);
  }, [currentConfig, handleNotificationChange, icons]);

  return (
    <div id='homebg' style={{ minHeight: '100vh' }}>
      {/* Country Selector */}
      <br></br><br></br><br></br>
      <div id='countryDropdown' className='d-flex align-items-center justify-content-center'>
        <Dropdown>
          <Dropdown.Toggle id="searchCountry">
            {currentConfig.flag} {currentConfig.displayName}
          </Dropdown.Toggle>
          <Dropdown.Menu id="docsDropdown" drop="down-centered">
            {Object.entries(countryConfig).map(([countryCode, config]) => (
              <Dropdown.Item
                id="docsDropdownItem"
                key={countryCode}
                onClick={() => handleCountryChange(countryCode)}
              >
                {config.flag} {config.displayName}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div id='feedDisplay'>
        <Container id='feedContainerFeedPage'>
          
          {/* Home Region Section */}
          {homeRegionSection}

          {/* Topic Notifications */}
          {topicNotifications}

        </Container>
      </div>

      {/* Fixed Bottom Navigation with proper spacing */}
      <div style={{ height: '80px' }}></div>
      
      <Navbar id='navbarMobileBottom' fixed="bottom" style={{ zIndex: 1030 }}>    
        <div id='changeBranchDropdownMobile'>
          <BottomNavbar />
        </div>
      </Navbar>
    </div>
  );
};

export default React.memo(NotificationSettingsPage);